import React from 'react';
import { inject, observer } from 'mobx-react';
import { Button, Flex, Text, Tooltip } from 'core/components';
import CodeMirror from 'core/components/CodeMirror';
import CopyToClipboard from 'app/components/CopyToClipboardButton';
import SudoTag from 'app/components/SudoTag';
import { FiCopy } from 'react-icons/fi';
import styled from 'styled-components';
import ToolResultCard from '../components/ToolResultCard';
import { SmallHeading } from '../KaizenChatSession.styles';
import { isDeviceSpecificErrorTool, isErroredToolCall } from '../util/toolErrorUtils';

const CodeWrap = styled.div`
  .CodeMirror {
    height: auto;
    font-size: 12px;
    border-radius: 4px;
  }

  .CodeMirror-scroll {
    max-height: 240px;
  }
`;

// Tool payloads arrive as objects, JSON strings, or plain text depending on the tool.
// An empty object/array counts as absent so callers can fall through to another field.
function formatPayload(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return formatPayload(JSON.parse(trimmed));
      } catch (e) {
        return value;
      }
    }
    return value;
  }

  if (typeof value === 'object' && Object.keys(value).length === 0) {
    return null;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
}

const DebugSection = ({ title, value, sudoOnly = false }) => {
  if (!value) {
    return null;
  }

  return (
    <Flex flexDirection="column" gap="4px">
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="center" gap="6px">
          <SmallHeading>{title}</SmallHeading>
          {sudoOnly && <SudoTag fontSize={11} />}
        </Flex>
        <Tooltip content={`Copy ${title.toLowerCase()}`} position="bottom">
          <CopyToClipboard text={value}>
            <Button minimal small icon={FiCopy} />
          </CopyToClipboard>
        </Tooltip>
      </Flex>
      <CodeWrap>
        <CodeMirror
          mode="javascript"
          value={value}
          options={{ readOnly: true, lineNumbers: false }}
          // Mounted inside an animating Collapse, so measure again once it has settled.
          editorDidMount={(editor) => setTimeout(() => editor.refresh(), 0)}
        />
      </CodeWrap>
    </Flex>
  );
};

/**
 * Fallback detail view for tools without a dedicated renderer — shows the raw call
 * input and output so a sub-agent's work can be inspected while debugging.
 * Raw errors are shown only to sudo users (a failed tool's processed result is a copy of its error).
 */
const ToolDebugResult = ({ $auth, tool, onCloseToolResult, isInlineMode = false }) => {
  const isFailed = isErroredToolCall(tool);
  const isErrorSafeToShow = isDeviceSpecificErrorTool(tool.tool_name);
  const isErrorSudoOnly = !isErrorSafeToShow && $auth.isSudoer;
  const canSeeError = isErrorSafeToShow || isErrorSudoOnly;

  const input = formatPayload(tool.query_object);
  const rawOutput = formatPayload(tool.raw_result);
  const processedOutput = !isFailed || canSeeError ? formatPayload(tool.processed_result) : null;
  const rawError = formatPayload(tool.error_message);
  const error = rawError && !canSeeError ? 'Something went wrong' : rawError;
  const hasAnything = input || rawOutput || processedOutput || error;

  return (
    <ToolResultCard
      tool={tool}
      onCloseToolResult={onCloseToolResult}
      className="tool-result-debug"
      title={tool.title || tool.tool_display_name || tool.tool_name}
      isInlineMode={isInlineMode}
      minHeight={isInlineMode ? 0 : 300}
      maxHeight={isInlineMode ? 500 : undefined}
      overflow={isInlineMode ? 'auto' : 'hidden'}
    >
      <Flex flexDirection="column" gap={2} p={2} pt={0}>
        <Flex alignItems="center" gap={2} flexWrap="wrap">
          <Text small muted>
            {tool.tool_name}
          </Text>
          {tool.latency_ms && (
            <Text small muted>
              {tool.latency_ms > 1000 ? `${(tool.latency_ms / 1000).toFixed(1)}s` : `${tool.latency_ms}ms`}
            </Text>
          )}
        </Flex>

        <DebugSection title="Input" value={input} />
        <DebugSection title="Output" value={rawOutput} />
        <DebugSection title="Processed result" value={processedOutput} sudoOnly={isFailed && isErrorSudoOnly} />
        <DebugSection title="Error" value={error} sudoOnly={isErrorSudoOnly} />

        {!hasAnything && <Text muted>No input or output was recorded for this tool call.</Text>}
      </Flex>
    </ToolResultCard>
  );
};

export default inject('$auth')(observer(ToolDebugResult));
